import { useQuery } from "@tanstack/react-query";
import { ProductRepository } from "./repositories/product-repository";
import { ClientProduct } from "./types/product";
import { Result, Button } from "antd";
import { ReactNode } from "react";
import { Spin } from "@/ui/spin";

export const useProduct = (id?: number) => {
  return useQuery({
    queryKey: ["product", id],
    queryFn: () => (id ? ProductRepository.fetchProduct(id) : null),
    staleTime: 600_000,
    enabled: !!id,
  });
};

type ProductStateProps = {
  isLoading: boolean;
  error: Error | null;
  isEmpty?: boolean;
  loadingComponent?: ReactNode;
  errorComponent?: ReactNode;
  emptyComponent?: ReactNode;
  children: ReactNode;
};

const ProductState = ({
  isLoading,
  error,
  isEmpty = false,
  loadingComponent,
  errorComponent,
  emptyComponent,
  children,
}: ProductStateProps) => {
  if (isLoading) {
    return loadingComponent ?? <Spin fullscreen />;
  }

  if (error) {
    return (
      errorComponent ?? (
        <Result
          status="error"
          title="Ошибка. Попробуйте позднее."
          subTitle={error.message}
          extra={
            <Button onClick={() => window.location.reload()}>Refresh</Button>
          }
        />
      )
    );
  }

  if (isEmpty) {
    return (
      emptyComponent ?? (
        <Result
          status="info"
          title="Product not found"
          subTitle="The requested product does not exist"
        />
      )
    );
  }

  return <>{children}</>;
};

export type ProductsProps = {
  product: ClientProduct;
  id: number;
};

export const withProduct = <P extends ProductsProps>(
  WrappedComponent: React.ComponentType<P>,
  options?: {
    loadingComponent?: ReactNode;
    errorComponent?: ReactNode;
    emptyComponent?: ReactNode;
    skipIfNoId?: boolean;
  },
) => {
  return (props: Omit<P, "product"> & { id?: number }) => {
    const { id, ...restProps } = props;
    const { data: product, isLoading, error } = useProduct(id);

    if (options?.skipIfNoId && !id) {
      return (
        <WrappedComponent {...(restProps as unknown as P)} product={null!} />
      );
    }

    return (
      <ProductState
        isLoading={isLoading}
        error={error}
        isEmpty={!product}
        loadingComponent={options?.loadingComponent}
        errorComponent={options?.errorComponent}
        emptyComponent={options?.emptyComponent}
      >
        <WrappedComponent {...(restProps as unknown as P)} product={product!} />
      </ProductState>
    );
  };
};
